<template>
  <div class="content-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderTournament :page="header" />
    <SecondaryNav
      v-if="nav.length"
      :items="nav"
    />
    <ContestIntro
      :infos="wallContest"
      :logged-in="authenticated"
      contest-type="contest"
    />
    <div
      v-if="wallContest?.winners?.length && countdownS(endDate) < 0"
      ref="les-gagnants"
    >
      <WinnersImage
        v-if="wallContest?.type === 1"
        :title="wallContest.winner_section_title"
        :posts="wallContest.winners"
      />
      <WinnersVideo
        v-else
        :title="wallContest.winner_section_title"
        :posts="wallContest.winners"
      />
    </div>
    <div
      v-if="wallContest?.winners && wallContest?.gifts?.length"
      ref="a-gagner"
      :class="{
        withWinner: wallContest.winners.length && countdownS(endDate) < 0,
        withoutWinner: !(wallContest.winners.length && countdownS(endDate) < 0),
      }"
    >
      <RewardsList
        :infos="wallContest"
        :logged-in="authenticated"
        contest-type="contest"
      />
    </div>
    <div
      v-if="hasArticle"
      ref="presentation"
    >
      <ContestArticle :page="article" />
    </div>
    <div
      v-if="wallContest?.how_to?.length && countdown(endDate) >= 0"
      ref="comment-participer"
      :class="{ hasNoArticle: !hasArticle }"
    >
      <ContestRules :rules="wallContest.how_to" />
    </div>
    <div ref="les-participations">
      <ContestGrid
        :posts="participations"
        :contest-type="wallContest?.type"
        :can-more="hasMorePosts"
        :key-index="participationKey"
        @select-post="fetchPost"
      />
    </div>
    <ContestSocial
      v-if="wallContest?.hide_comments === false"
      :page="contestSocial"
    />
    <ContestDetails
      v-if="
        wallContest?.highlight ||
        wallContest?.highlight_title ||
        wallContest?.highlight_content
      "
      :details="{
        highlight: wallContest.highlight,
        highlight_title: wallContest.highlight_title,
        highlight_content: wallContest.highlight_content,
        highlight_button_label: wallContest.highlight_button_label,
        highlight_button_link: wallContest.highlight_button_link,
        highlight_bg_desktop: wallContest.highlight_bg_desktop,
        highlight_bg_mobile: wallContest.highlight_bg_mobile,
      }"
    />
    <DoNotMissSection
      v-if="doNotMiss.length"
      :slider-items="doNotMiss"
    />
    <ContestZoomItem
      :show="zoom"
      :post="currentPost"
      :social="social"
      @change-show="zoom = false"
    />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadWallContest = useLoadWallContest()
    return loadWallContest(to)
  },
})

const { authenticated } = useAuthenticated()
const wallContestsStore = useWallContestsStore()
const { wallContest, currentPost } = storeToRefs(wallContestsStore)

const participations = computed(() => {
  return wallContest.value?.random_posts
})

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const socialContestsStore = useSocialContestStore()
const { social } = storeToRefs(socialContestsStore)

const loadWallContest = useLoadWallContest()

const {
  arianeRoute,
  header,
  endDate,
  hasArticle,
  defaultArticle,
  defaultContestSocial,
} = useContest({ contest: wallContest, loader: loadWallContest })

const zoom = ref(false)
const participationKey = ref(0)

const hasMorePosts = computed(() => {
  return wallContest.value?.has_more_wc_posts
})

const nav = computed(() => {
  const nav = []

  if (wallContest.value?.winners?.length && countdownS(endDate.value) < 0) {
    nav.push({
      label: 'Les gagnants',
      ref: 'les-gagnants',
    })
  }

  if (wallContest.value?.gifts?.length) {
    nav.push({
      label: 'à gagner',
      ref: 'a-gagner',
    })
  }

  if (hasArticle.value) {
    nav.push({
      label: 'présentation',
      ref: 'presentation',
    })
  }

  if (wallContest.value?.how_to?.length && countdown(endDate.value) >= 0) {
    nav.push({
      label: 'Comment participer ?',
      ref: 'comment-participer',
    })
  }

  nav.push({
    label: 'Les participations',
    ref: 'les-participations',
  })

  return nav
})

const article = computed(() => {
  return {
    ...defaultArticle.value,
    type: 'wallContest',
  }
})

const contestSocial = computed(() => {
  return {
    ...defaultContestSocial.value,
    type: 'wallContest',
  }
})

const fetchPost = async (slug: string) => {
  await wallContestsStore.fetchWallContestPost({
    slug,
  })
  zoom.value = true
}
</script>

<style lang="scss" scoped>
.content-page {
  padding-bottom: 80px;

  @media screen and (max-width: 700px) {
    padding-bottom: 40px;
  }

  .hasNoArticle {
    margin-top: 60px;
  }

  .withWinner {
    margin-top: -130px;
  }

  .withoutWinner {
    margin-top: -70px;
  }

  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }
    }
  }

  hr {
    height: 1px;
    margin: 8rem 8rem 0;
    background-color: rgba($black, 0.2);
    border: 0;
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 1024px) {
  .content-page {
    hr.separator {
      display: none;
    }
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
